import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Table, Alert, Spinner, Card, Tabs, Tab } from "react-bootstrap";

const HistorialCaja = () => {
  const { idBox } = useParams();

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [qrOperaciones, setQRoperaciones] = useState([]);
  const [saldosVendidos, setSaldosVendidos] = useState([]);
  const [totalQRAprobados, setTotalQRAprobados] = useState(null);
  const [totalQRRechazados, setTotalQRRechazados] = useState(null);

  // Estado para controlar la pestaña activa (por defecto "saldos")
  const [activeTab, setActiveTab] = useState("saldos");

  useEffect(() => {
    fetchHistorialCaja();
  }, [idBox]);

  const fetchHistorialCaja = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/public/v2/List/History`,
        {
          idBox: idBox,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "SERVICIOS-URBANOS-TOKEN": localStorage.getItem("token"),
          },
        }
      );

      if (response.status === 200 && response.data.ok) {
        const movimientosData = response.data;
        const qrOperaciones = movimientosData.qr_operaciones;
        const saldoVendido = movimientosData.saldo_vendido;
        setQRoperaciones(qrOperaciones || []);
        setSaldosVendidos(saldoVendido || []);

        // Suma total de los registros con typeQR "accepted"
        const totalAccepted = qrOperaciones
          .filter((qr) => qr.state === "APPROVED")
          .reduce((acc, qr) => acc + (Number(qr.amount) || 0), 0);
        setTotalQRAprobados(totalAccepted || null);

        // Suma total de los registros con typeQR "rejected"
        const totalRejected = qrOperaciones
          .filter((qr) => qr.state === "REJECTED")
          .reduce((acc, qr) => acc + (Number(qr.amount) || 0), 0);
        setTotalQRRechazados(totalRejected || null);
      }
    } catch (error) {
      console.error(
        "Error fetching historial de caja:",
        error.response?.data?.msg || error
      );
      setError(
        error.response?.data?.msg || "Error al obtener el historial de caja"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const formatCash = (cash) => {
    if (typeof cash === "number") {
      return cash.toFixed(2);
    }
    return "0.00";
  };
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(amount || 0);
  };
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = String(date.getUTCFullYear()).slice(-2);
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <Card className="historial-de-cajas-card">
      <Card.Body>
        <h3 className="mb-4">Historial de Caja {idBox}</h3>

        {error && <Alert variant="danger">{error}</Alert>}

        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Cargando historial...</span>
            </Spinner>
            <p className="mt-2">Cargando historial...</p>
          </div>
        ) : (
          error == "" && (
            <Tabs
              id="historial-caja-tabs"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
              fill
            >
              {/* Fichero 1: Saldos Vendidos */}
              <Tab eventKey="saldos" title={`Saldos Vendidos `}>
                {saldosVendidos.length === 0 ? (
                  <Alert variant="info" className="mt-3">
                    No hay registros de saldos vendidos.
                  </Alert>
                ) : (
                  <Table striped bordered hover responsive className="mt-3">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Apellido</th>
                        <th>Balance</th>
                        <th>CVU</th>
                        <th>CUIT</th>
                        <th>Tipo de Movimiento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {saldosVendidos.map((mov, index) => (
                        <tr key={index}>
                          <td>{mov.movimiento?.nameOrig}</td>
                          <td>{mov.movimiento?.lastNameOrig}</td>
                          <td>${formatCash(mov.movimiento?.boxCash)}</td>
                          <td>{mov.movimiento?.cvu}</td>
                          <td>{mov.movimiento?.cuit}</td>
                          <td>{mov.movimiento?.types}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              {/* Fichero 2: Operaciones QR */}
              <Tab eventKey="qr" title={`Operaciones QR `}>
                {qrOperaciones.length === 0 ? (
                  <Alert variant="info" className="mt-3">
                    No hay registros de operaciones QR.
                  </Alert>
                ) : (
                  <div>
                    <div className="p-3 border rounded bg-light d-flex justify-content-center  align-items-center">
                      <p className="mb-0 me-3">
                        <strong>Total operaciones aceptadas:</strong>{" "}
                        <span className="text-success fw-bold">
                          {formatCurrency(totalQRAprobados)}
                        </span>
                      </p>
                      <p className="mb-0">
                        <strong>Total operaciones rechazadas:</strong>{" "}
                        <span className="text-danger fw-bold">
                          {formatCurrency(totalQRRechazados)}
                        </span>
                      </p>
                    </div>
                    <Table striped bordered hover responsive className="mt-3">
                      <thead>
                        <tr>
                          <th>Monto</th>
                          <th>Fecha</th>
                          <th>Estado</th>
                          <th>Tipo QR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {qrOperaciones.map((op, index) => (
                          <tr key={index}>
                            <td>${formatCash(op.amount)}</td>
                            <td>{formatDate(op.date)}</td>
                            <td>{op.state}</td>
                            <td>{op.typeQR}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Tab>
            </Tabs>
          )
        )}
      </Card.Body>
    </Card>
  );
};

export default HistorialCaja;
